/**
 * useClauseDetail.ts
 *
 * Fetches and caches clause body + page images for the expanded node card.
 * Module-level cache survives re-renders; invalidated by calling clearClauseDetailCache()
 * when a new query arrives.
 */

import { useEffect, useState } from 'react';
import { fetchClauseFull, fetchClausePages } from '../api';
import type { PageInfo } from '../types';

// ClauseFull is the return type of fetchClauseFull — define it locally to
// avoid importing the internal api type.
export interface ClauseFull {
  id: string;
  standard: string;
  clause_number: string | null;
  table_number: string | null;
  figure_number: string | null;
  title: string;
  body: string;
  skill: string | null;
}

export interface ClauseDetail {
  full?: ClauseFull;
  pages?: PageInfo[];
  loading: boolean;
  error?: string;
}

// Module-level cache — survives component re-renders within a session
const cache = new Map<string, { full: ClauseFull; pages: PageInfo[] }>();

/** Call when a new query arrives to invalidate stale clause bodies */
export function clearClauseDetailCache(): void {
  cache.clear();
}

export function useClauseDetail(clauseId: string | null): ClauseDetail {
  const [detail, setDetail] = useState<ClauseDetail>({ loading: false });

  useEffect(() => {
    if (!clauseId) {
      setDetail({ loading: false });
      return;
    }

    const cached = cache.get(clauseId);
    if (cached) {
      setDetail({ full: cached.full, pages: cached.pages, loading: false });
      return;
    }

    setDetail({ loading: true });

    let cancelled = false;

    Promise.all([
      fetchClauseFull(clauseId),
      fetchClausePages(clauseId),
    ])
      .then(([full, pages]) => {
        if (cancelled) return;
        cache.set(clauseId, { full, pages });
        setDetail({ full, pages, loading: false });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setDetail({
          loading: false,
          error: err instanceof Error ? err.message : 'Failed to load clause',
        });
      });

    return () => { cancelled = true; };
  }, [clauseId]);

  return detail;
}
